import { CodeBlock, InfoBox, SectionTitle, SubsectionTitle, Table, Badge } from './shared';

export function TroubleshootingContent() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-white mb-4">故障排除</h1>
        <p className="text-lg text-slate-400 leading-relaxed">
          遇到问题？这里提供了常见问题的解决方案和调试技巧，帮助你快速定位和解决网关相关问题。
        </p>
      </div>

      <SectionTitle id="common-issues">常见问题</SectionTitle>
      
      <SubsectionTitle id="startup-issues">启动问题</SubsectionTitle>
      <div className="space-y-4">
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <div className="flex items-start gap-3 mb-4">
            <Badge variant="error">错误</Badge>
            <h4 className="text-white font-medium">网关无法启动</h4>
          </div>
          <p className="text-slate-300 mb-4">错误信息：<code className="px-1.5 py-0.5 bg-slate-800 rounded text-red-400">Failed to start gateway: address already in use</code></p>
          
          <div className="space-y-3">
            <div>
              <p className="text-slate-400 text-sm mb-2"><strong>原因：</strong>端口已被占用</p>
              <CodeBlock
                language="bash"
                code={`# 检查端口占用
sudo lsof -i :3000
# 或
sudo netstat -tulpn | grep 3000

# 终止占用端口的进程
sudo kill -9 <PID>

# 或修改网关端口
openclaw gateway config set gateway.port 3001
openclaw gateway restart`}
              />
            </div>
          </div>
        </div>

        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <div className="flex items-start gap-3 mb-4">
            <Badge variant="error">错误</Badge>
            <h4 className="text-white font-medium">配置文件加载失败</h4>
          </div>
          <p className="text-slate-300 mb-4">错误信息：<code className="px-1.5 py-0.5 bg-slate-800 rounded text-red-400">Failed to load config: invalid YAML syntax</code></p>
          
          <div className="space-y-3">
            <div>
              <p className="text-slate-400 text-sm mb-2"><strong>解决方案：</strong></p>
              <CodeBlock
                language="bash"
                code={`# 验证配置文件语法
openclaw gateway config validate

# 查看详细错误信息
openclaw gateway config validate --verbose

# 使用默认配置重新生成
openclaw gateway config init --force

# 从备份恢复
cp ~/.openclaw/gateway.yaml.backup ~/.openclaw/gateway.yaml`}
              />
            </div>
          </div>
        </div>

        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <div className="flex items-start gap-3 mb-4">
            <Badge variant="error">错误</Badge>
            <h4 className="text-white font-medium">SSL 证书错误</h4>
          </div>
          <p className="text-slate-300 mb-4">错误信息：<code className="px-1.5 py-0.5 bg-slate-800 rounded text-red-400">Failed to load SSL certificate</code></p>
          
          <div className="space-y-3">
            <div>
              <p className="text-slate-400 text-sm mb-2"><strong>检查步骤：</strong></p>
              <CodeBlock
                language="bash"
                code={`# 检查证书文件是否存在
ls -la /etc/openclaw/ssl/

# 检查证书有效性
openssl x509 -in /etc/openclaw/ssl/cert.pem -text -noout

# 检查证书和私钥是否匹配
openssl x509 -noout -modulus -in cert.pem | openssl md5
openssl rsa -noout -modulus -in key.pem | openssl md5

# 检查文件权限
sudo chmod 644 /etc/openclaw/ssl/cert.pem
sudo chmod 600 /etc/openclaw/ssl/key.pem`}
              />
            </div>
          </div>
        </div>
      </div>

      <SubsectionTitle id="auth-issues">认证问题</SubsectionTitle>
      <div className="space-y-4">
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <div className="flex items-start gap-3 mb-4">
            <Badge variant="warning">401</Badge>
            <h4 className="text-white font-medium">认证失败</h4>
          </div>
          <p className="text-slate-300 mb-4">错误信息：<code className="px-1.5 py-0.5 bg-slate-800 rounded text-yellow-400">Unauthorized: Invalid API key</code></p>
          
          <div className="space-y-3">
            <div>
              <p className="text-slate-400 text-sm mb-2"><strong>排查步骤：</strong></p>
              <CodeBlock
                language="bash"
                code={`# 1. 验证 API Key 是否有效
openclaw gateway api-key show sk_live_abc123...

# 2. 检查 API Key 是否过期
openclaw gateway api-key list --expired

# 3. 检查请求头格式
curl -v -X POST http://localhost:3000/api/chat \\
  -H "Authorization: Bearer sk_live_abc123..." \\
  -d '{"message": "test"}'

# 4. 查看认证日志
openclaw gateway logs --search "auth_failure" --tail 50

# 5. 测试创建新的 API Key
openclaw gateway api-key create --name "test"`}
              />
            </div>
          </div>
        </div>

        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <div className="flex items-start gap-3 mb-4">
            <Badge variant="warning">401</Badge>
            <h4 className="text-white font-medium">JWT Token 无效</h4>
          </div>
          <p className="text-slate-300 mb-4">错误信息：<code className="px-1.5 py-0.5 bg-slate-800 rounded text-yellow-400">Invalid JWT token</code></p>
          
          <div className="space-y-3">
            <div>
              <p className="text-slate-400 text-sm mb-2"><strong>检查项：</strong></p>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-blank-circle-line text-green-400 text-xs mt-1"></i>
                  <span>Token 是否过期（检查 exp 字段）</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-blank-circle-line text-green-400 text-xs mt-1"></i>
                  <span>JWT 密钥是否正确配置</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-blank-circle-line text-green-400 text-xs mt-1"></i>
                  <span>签名算法是否匹配</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-blank-circle-line text-green-400 text-xs mt-1"></i>
                  <span>Token 格式是否正确（Bearer + Token）</span>
                </li>
              </ul>
              <CodeBlock
                language="bash"
                code={`# 解码 JWT Token 查看内容
echo "eyJhbGc..." | base64 -d | jq '.'

# 使用在线工具验证：https://jwt.io

# 检查 JWT 配置
openclaw gateway config show auth.jwt`}
              />
            </div>
          </div>
        </div>
      </div>

      <SubsectionTitle id="rate-limit-issues">限流问题</SubsectionTitle>
      <div className="space-y-4">
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <div className="flex items-start gap-3 mb-4">
            <Badge variant="warning">429</Badge>
            <h4 className="text-white font-medium">请求被限流</h4>
          </div>
          <p className="text-slate-300 mb-4">错误信息：<code className="px-1.5 py-0.5 bg-slate-800 rounded text-yellow-400">Rate limit exceeded</code></p>
          
          <div className="space-y-3">
            <div>
              <p className="text-slate-400 text-sm mb-2"><strong>解决方案：</strong></p>
              <CodeBlock
                language="bash"
                code={`# 查看当前限流配置
openclaw gateway config show rate_limit

# 查看 API Key 的限流状态
openclaw gateway api-key stats sk_live_abc123...

# 临时提高限流阈值
openclaw gateway config set rate_limit.requests_per_minute 120

# 为特定 API Key 设置更高限流
openclaw gateway api-key update sk_live_abc123... --rate-limit 200

# 查看限流日志
openclaw gateway logs --search "rate_limit_exceeded"`}
              />
            </div>
          </div>
        </div>
      </div>

      <SubsectionTitle id="performance-issues">性能问题</SubsectionTitle>
      <div className="space-y-4">
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <div className="flex items-start gap-3 mb-4">
            <Badge variant="warning">慢</Badge>
            <h4 className="text-white font-medium">响应时间过长</h4>
          </div>
          
          <div className="space-y-3">
            <div>
              <p className="text-slate-400 text-sm mb-2"><strong>诊断步骤：</strong></p>
              <CodeBlock
                language="bash"
                code={`# 1. 查看性能指标
openclaw gateway stats --metric latency

# 2. 查看慢请求日志
openclaw gateway logs --slow --threshold 1000  # 超过 1 秒

# 3. 启用性能分析
openclaw gateway config set debug.profiling true
openclaw gateway restart

# 4. 查看系统资源使用
top -p $(pgrep openclaw-gateway)
# 或
htop

# 5. 检查网络延迟
ping backend-server
traceroute backend-server

# 6. 查看数据库连接
openclaw gateway stats --metric db_connections`}
              />
            </div>
            
            <div>
              <p className="text-slate-400 text-sm mb-2"><strong>优化建议：</strong></p>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-400 mt-0.5"></i>
                  <span>启用缓存减少后端请求</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-400 mt-0.5"></i>
                  <span>增加工作进程数量</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-400 mt-0.5"></i>
                  <span>使用连接池</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-400 mt-0.5"></i>
                  <span>优化数据库查询</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-400 mt-0.5"></i>
                  <span>使用 CDN 加速静态资源</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <div className="flex items-start gap-3 mb-4">
            <Badge variant="error">高</Badge>
            <h4 className="text-white font-medium">内存使用过高</h4>
          </div>
          
          <div className="space-y-3">
            <div>
              <p className="text-slate-400 text-sm mb-2"><strong>排查步骤：</strong></p>
              <CodeBlock
                language="bash"
                code={`# 查看内存使用
openclaw gateway stats --metric memory

# 查看详细内存分析
openclaw gateway debug memory

# 检查是否有内存泄漏
openclaw gateway debug heap-dump --output heap.prof

# 分析堆转储文件
go tool pprof heap.prof

# 临时措施：重启网关
openclaw gateway restart`}
              />
            </div>
            
            <div>
              <p className="text-slate-400 text-sm mb-2"><strong>优化配置：</strong></p>
              <CodeBlock
                language="yaml"
                code={`# gateway.yaml
performance:
  # 限制最大内存使用
  max_memory: "2GB"
  
  # 启用内存回收
  gc:
    enabled: true
    interval: 300  # 5 分钟
  
  # 限制缓存大小
  cache:
    max_size: "500MB"
    eviction_policy: "lru"`}
              />
            </div>
          </div>
        </div>
      </div>

      <SectionTitle id="connection-issues">连接问题</SectionTitle>
      <div className="space-y-4">
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <div className="flex items-start gap-3 mb-4">
            <Badge variant="error">错误</Badge>
            <h4 className="text-white font-medium">无法连接到后端服务</h4>
          </div>
          <p className="text-slate-300 mb-4">错误信息：<code className="px-1.5 py-0.5 bg-slate-800 rounded text-red-400">Connection refused</code></p>
          
          <div className="space-y-3">
            <div>
              <p className="text-slate-400 text-sm mb-2"><strong>检查清单：</strong></p>
              <CodeBlock
                language="bash"
                code={`# 1. 检查后端服务是否运行
curl http://backend-server:8080/health

# 2. 检查网络连通性
ping backend-server
telnet backend-server 8080

# 3. 检查防火墙规则
sudo iptables -L
sudo ufw status

# 4. 检查 DNS 解析
nslookup backend-server
dig backend-server

# 5. 查看网关日志
openclaw gateway logs --search "connection refused"

# 6. 测试负载均衡配置
openclaw gateway config show load_balancing`}
              />
            </div>
          </div>
        </div>

        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <div className="flex items-start gap-3 mb-4">
            <Badge variant="warning">超时</Badge>
            <h4 className="text-white font-medium">请求超时</h4>
          </div>
          <p className="text-slate-300 mb-4">错误信息：<code className="px-1.5 py-0.5 bg-slate-800 rounded text-yellow-400">Request timeout</code></p>
          
          <div className="space-y-3">
            <div>
              <p className="text-slate-400 text-sm mb-2"><strong>调整超时配置：</strong></p>
              <CodeBlock
                language="yaml"
                code={`# gateway.yaml
timeout:
  # 连接超时
  connect: 10  # 秒
  # 读取超时
  read: 60
  # 写入超时
  write: 60
  # 空闲超时
  idle: 300

# 应用配置
openclaw gateway reload`}
              />
            </div>
          </div>
        </div>
      </div>

      <SectionTitle id="debugging-tools">调试工具</SectionTitle>
      <SubsectionTitle id="debug-mode">调试模式</SubsectionTitle>
      <CodeBlock
        language="bash"
        code={`# 启用调试模式
openclaw gateway config set logging.level debug
openclaw gateway reload

# 启用请求追踪
openclaw gateway config set debug.trace_requests true

# 查看详细日志
openclaw gateway logs --level debug --follow

# 启用性能分析
openclaw gateway config set debug.profiling true

# 生成性能报告
openclaw gateway debug profile --duration 60s --output profile.html`}
      />

      <SubsectionTitle id="health-check">健康检查</SubsectionTitle>
      <CodeBlock
        language="bash"
        code={`# 检查网关健康状态
curl http://localhost:3000/health

# 响应示例
{
  "status": "healthy",
  "version": "1.0.0",
  "uptime": 3600,
  "checks": {
    "database": "ok",
    "redis": "ok",
    "backend": "ok"
  }
}

# 详细健康检查
openclaw gateway health --verbose

# 检查特定组件
openclaw gateway health --component database`}
      />

      <SubsectionTitle id="request-tracing">请求追踪</SubsectionTitle>
      <CodeBlock
        language="bash"
        code={`# 追踪特定请求
curl -X POST http://localhost:3000/api/chat \\
  -H "X-Trace-ID: trace_abc123" \\
  -H "Authorization: Bearer sk_live_..." \\
  -d '{"message": "test"}'

# 查看请求追踪
openclaw gateway trace show trace_abc123

# 输出示例
Request Trace: trace_abc123
├─ Gateway Received: 2024-01-01T12:00:00.000Z
├─ Auth Check: 2ms
├─ Rate Limit Check: 1ms
├─ Backend Request: 2024-01-01T12:00:00.003Z
│  └─ Duration: 120ms
├─ Response Sent: 2024-01-01T12:00:00.123Z
└─ Total Duration: 123ms`}
      />

      <SectionTitle id="log-analysis">日志分析</SectionTitle>
      <SubsectionTitle id="error-patterns">错误模式分析</SubsectionTitle>
      <CodeBlock
        language="bash"
        code={`# 统计错误类型
openclaw gateway logs analyze --metric error_types

# 查找错误峰值
openclaw gateway logs analyze --metric error_spikes

# 分析错误趋势
openclaw gateway logs analyze --metric error_trend --from "2024-01-01"

# 生成错误报告
openclaw gateway logs analyze --report errors --output error-report.html`}
      />

      <SubsectionTitle id="performance-analysis">性能分析</SubsectionTitle>
      <CodeBlock
        language="bash"
        code={`# 分析响应时间分布
openclaw gateway logs analyze --metric latency_distribution

# 查找慢请求
openclaw gateway logs analyze --metric slow_requests --threshold 1000

# 分析瓶颈
openclaw gateway logs analyze --metric bottlenecks

# 生成性能报告
openclaw gateway logs analyze --report performance --output perf-report.html`}
      />

      <SectionTitle id="recovery-procedures">恢复流程</SectionTitle>
      <SubsectionTitle id="backup-restore">备份与恢复</SubsectionTitle>
      <CodeBlock
        language="bash"
        code={`# 备份配置
openclaw gateway backup create --name "before-update"

# 列出备份
openclaw gateway backup list

# 恢复配置
openclaw gateway backup restore before-update

# 自动备份配置
openclaw gateway config set backup.enabled true
openclaw gateway config set backup.interval "daily"
openclaw gateway config set backup.retention 30  # 保留 30 天`}
      />

      <SubsectionTitle id="rollback">回滚操作</SubsectionTitle>
      <CodeBlock
        language="bash"
        code={`# 查看配置历史
openclaw gateway config history

# 回滚到上一个版本
openclaw gateway config rollback

# 回滚到特定版本
openclaw gateway config rollback --version 5

# 查看回滚前后差异
openclaw gateway config diff --version 5 --version 6`}
      />

      <SectionTitle id="support">获取支持</SectionTitle>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <i className="ri-book-line text-green-400"></i>
            文档资源
          </h3>
          <ul className="space-y-2 text-slate-300 text-sm">
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-green-400 mt-0.5"></i>
              <a href="https://openclaw.cc/docs" className="hover:text-green-400 transition-colors">官方文档</a>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-green-400 mt-0.5"></i>
              <a href="https://openclaw.cc/api" className="hover:text-green-400 transition-colors">API 参考</a>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-green-400 mt-0.5"></i>
              <a href="https://openclaw.cc/examples" className="hover:text-green-400 transition-colors">示例代码</a>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-green-400 mt-0.5"></i>
              <a href="https://openclaw.cc/faq" className="hover:text-green-400 transition-colors">常见问题</a>
            </li>
          </ul>
        </div>

        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <i className="ri-team-line text-green-400"></i>
            社区支持
          </h3>
          <ul className="space-y-2 text-slate-300 text-sm">
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-green-400 mt-0.5"></i>
              <a href="https://github.com/openclaw/gateway" className="hover:text-green-400 transition-colors">GitHub Issues</a>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-green-400 mt-0.5"></i>
              <a href="https://discord.gg/openclaw" className="hover:text-green-400 transition-colors">Discord 社区</a>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-green-400 mt-0.5"></i>
              <a href="https://forum.openclaw.cc" className="hover:text-green-400 transition-colors">论坛讨论</a>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-green-400 mt-0.5"></i>
              <a href="mailto:support@openclaw.cc" className="hover:text-green-400 transition-colors">邮件支持</a>
            </li>
          </ul>
        </div>
      </div>

      <InfoBox type="info" title="提交问题时请提供">
        <ul className="space-y-1 mt-2">
          <li>• OpenClaw 版本号</li>
          <li>• 操作系统和版本</li>
          <li>• 完整的错误信息和堆栈</li>
          <li>• 相关的配置文件（脱敏后）</li>
          <li>• 重现问题的步骤</li>
          <li>• 相关的日志片段</li>
        </ul>
      </InfoBox>

      <SectionTitle id="diagnostic-report">生成诊断报告</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        在联系支持前，可以生成完整的诊断报告：
      </p>
      <CodeBlock
        language="bash"
        code={`# 生成诊断报告
openclaw gateway diagnostic --output diagnostic-report.zip

# 报告包含：
# - 系统信息
# - 配置文件（脱敏）
# - 最近的日志
# - 性能指标
# - 健康检查结果
# - 错误统计

# 查看报告内容
unzip -l diagnostic-report.zip`}
      />
    </div>
  );
}

export default TroubleshootingContent;